/**
 * InfiniteSpiral - Vanilla JS Implementation
 * Adapted from React Bits for Shopify / HTML
 */

(function (window) {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const modulo = (value, divisor) => ((value % divisor) + divisor) % divisor;
  const smoothstep = (min, max, value) => {
    const x = clamp((value - min) / (max - min || 1), 0, 1);
    return x * x * (3 - 2 * x);
  };

  class InfiniteSpiralApp {
    constructor(container, options = {}) {
      this.container = container;
      this.items = options.items || [];
      this.speed = options.speed !== undefined ? options.speed : 0.55;
      this.direction = options.direction || 'up';
      this.animationMode = options.animationMode || 'all';
      this.radius = options.radius !== undefined ? options.radius : 170;
      this.cardWidth = options.cardWidth || 130;
      this.cardHeight = options.cardHeight || 160;
      this.verticalSpacing = options.verticalSpacing || 65;
      this.perspective = options.perspective || 1000;
      this.cardsPerTurn = options.cardsPerTurn || 7;
      this.rotation = options.rotation || 0;
      this.cardTilt = options.cardTilt || 0;
      this.cardRadius = options.cardRadius || 12;
      this.centerScale = options.centerScale || 1.2;
      this.edgeFade = options.edgeFade || 0.3;
      this.edgeBlur = options.edgeBlur || 6;
      this.pauseOnHover = options.pauseOnHover !== undefined ? options.pauseOnHover : true;
      this.imageFit = options.imageFit || 'cover';
      this.grayscale = options.grayscale || 0;

      this.progress = 0;
      this.targetProgress = 0;
      this.autoSpeed = 0;
      this.isHovered = false;
      this.isVisible = true;
      this.isDragging = false;
      this.lastPointerY = 0;
      this.dragMoved = false;

      this.cardNodes = [];

      this.init();
    }

    init() {
      if (!this.container || this.items.length === 0) return;

      this.normalizedItems = this.items.map((item, index) => {
        if (typeof item === 'string') {
          return { src: item, alt: `Spiral image ${index + 1}`, href: '#' };
        }
        return { alt: item.title || `Spiral item ${index + 1}`, href: '#', ...item };
      });

      this.buildDOM();
      this.addEventListeners();
      this.startLoop();
    }

    buildDOM() {
      this.container.classList.add('infinite-spiral');
      this.container.style.perspective = `${this.perspective}px`;
      this.container.style.setProperty('--infinite-spiral-card-width', `${this.cardWidth}px`);
      this.container.style.setProperty('--infinite-spiral-card-height', `${this.cardHeight}px`);
      this.container.style.setProperty('--infinite-spiral-card-radius', `${this.cardRadius}px`);

      const dragEnabled = this.animationMode === 'drag' || this.animationMode === 'all';
      this.container.style.cursor = dragEnabled ? 'grab' : 'default';
      this.container.style.touchAction = dragEnabled ? 'pan-x' : 'auto';
      this.container.style.userSelect = dragEnabled ? 'none' : 'auto';

      this.stage = document.createElement('div');
      this.stage.className = 'infinite-spiral__stage';
      this.stage.setAttribute('role', 'list');
      this.stage.setAttribute('aria-label', 'Infinite spiral discount products gallery');

      this.cardNodes = this.normalizedItems.map((item, index) => {
        const card = document.createElement('a');
        card.className = 'infinite-spiral__item';
        card.style.width = `${this.cardWidth}px`;
        card.style.height = `${this.cardHeight}px`;
        card.style.borderRadius = `${this.cardRadius}px`;
        card.href = item.href || item.url || '#';
        card.setAttribute('role', 'listitem');
        card.setAttribute('aria-label', item.label || item.title || item.alt);

        const img = document.createElement('img');
        img.className = 'infinite-spiral__image';
        img.src = item.src || item.image;
        img.alt = item.alt || item.title || '';
        img.loading = index < 6 ? 'eager' : 'lazy';
        img.draggable = false;
        img.style.objectFit = this.imageFit;
        if (this.grayscale > 0) {
          img.style.filter = `grayscale(${Math.min(1, Math.max(0, this.grayscale))})`;
        }
        card.appendChild(img);

        // Price Badge overlay on card if price is provided!
        if (item.price) {
          const badge = document.createElement('div');
          badge.className = 'infinite-spiral__price-tag';
          let html = `<span class="infinite-spiral__price">${item.price}</span>`;
          if (item.compare_at_price) {
            html += `<s class="infinite-spiral__compare-price">${item.compare_at_price}</s>`;
          }
          badge.innerHTML = html;
          card.appendChild(badge);
        }

        this.stage.appendChild(card);
        return card;
      });

      this.container.appendChild(this.stage);
    }

    startLoop() {
      let previousTime = performance.now();
      let bounds = this.container.getBoundingClientRect();
      const scrollEnabled = this.animationMode === 'scroll' || this.animationMode === 'all';
      const scrollSpeedMultiplier = Math.max(this.speed, 0) / 0.55;
      let lastScrollY = window.scrollY;

      this.resizeObserver = new ResizeObserver(() => {
        if (this.container) bounds = this.container.getBoundingClientRect();
      });
      this.resizeObserver.observe(this.container);

      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting;
        },
        { threshold: 0.02 }
      );
      this.intersectionObserver.observe(this.container);

      this.onScroll = () => {
        const nextScrollY = window.scrollY;
        const scrollDelta = nextScrollY - lastScrollY;
        lastScrollY = nextScrollY;
        if (!scrollEnabled || !this.isVisible || scrollDelta === 0) return;
        this.targetProgress += clamp(
          (scrollDelta * scrollSpeedMultiplier) / Math.max(this.verticalSpacing * 2, 1),
          -1.5,
          1.5
        );
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });

      const render = (time) => {
        const delta = Math.min((time - previousTime) / 1000, 0.05);
        previousTime = time;

        const autoEnabled = this.animationMode === 'auto' || this.animationMode === 'all';
        const motionPaused = this.isDragging || (this.pauseOnHover && this.isHovered);
        const directionMultiplier = this.direction === 'down' ? -1 : 1;
        const desiredAutoSpeed =
          autoEnabled && this.isVisible && !motionPaused
            ? this.speed * directionMultiplier
            : 0;

        const speedBlend = 1 - Math.exp(-delta * 7);
        this.autoSpeed += (desiredAutoSpeed - this.autoSpeed) * speedBlend;
        this.targetProgress += this.autoSpeed * delta;

        const followBlend = 1 - Math.exp(-delta * (this.isDragging ? 22 : 11));
        this.progress += (this.targetProgress - this.progress) * followBlend;

        const count = this.normalizedItems.length;
        const half = count / 2;
        const width = Math.max(bounds.width, 1);
        const height = Math.max(bounds.height, 1);
        const fit = Math.min(1, width / (this.cardWidth * 2.8), height / (this.cardHeight * 2.35));
        const responsiveRadius = Math.min(this.radius, Math.max(65, width * 0.36)) * fit;
        const fadeStart = clamp(1 - this.edgeFade, 0, 0.98);
        const turnSize = Math.max(this.cardsPerTurn, 1);

        this.cardNodes.forEach((card, index) => {
          if (!card) return;
          let offset = index - this.progress;
          offset = modulo(offset + half, count) - half;

          const edge = Math.min(Math.abs(offset) / Math.max(half, 1), 1);
          const opacity = 1 - smoothstep(fadeStart, 1, edge);
          const focus = 1 - Math.min(Math.abs(offset) / Math.max(turnSize * 0.65, 1), 1);
          const scale = (1 + (this.centerScale - 1) * focus) * fit;
          const angle = offset * (360 / turnSize) + this.rotation;
          const angleRadians = (angle * Math.PI) / 180;
          const x = Math.sin(angleRadians) * responsiveRadius;
          const z = Math.cos(angleRadians) * responsiveRadius;
          const depthScale = clamp(this.perspective / Math.max(this.perspective - z, 1), 0.72, 1.45);
          const visualScale = scale * depthScale;
          const depth = (z / Math.max(responsiveRadius, 1) + 1) / 2;
          const blur = this.edgeBlur * smoothstep(0.35, 1, edge);

          card.style.transform = `translate(-50%, -50%) translate3d(${x}px, ${offset * this.verticalSpacing * fit}px, 0) rotateZ(${this.cardTilt}deg) scale(${visualScale})`;
          card.style.opacity = opacity.toFixed(3);
          card.style.filter = blur > 0.01 ? `blur(${blur.toFixed(2)}px)` : 'none';
          card.style.zIndex = String(Math.round(depth * 100000) + index);
          card.style.pointerEvents = opacity > 0.25 ? 'auto' : 'none';
        });

        this.rafId = requestAnimationFrame(render);
      };

      this.rafId = requestAnimationFrame(render);
    }

    addEventListeners() {
      const dragEnabled = this.animationMode === 'drag' || this.animationMode === 'all';

      this.onMouseEnter = () => { this.isHovered = true; };
      this.onMouseLeave = () => { this.isHovered = false; };

      this.onPointerDown = (event) => {
        if (!dragEnabled || event.button !== 0) return;
        this.isDragging = true;
        this.dragMoved = false;
        this.lastPointerY = event.clientY;
        this.targetProgress = this.progress;
        if (event.currentTarget.setPointerCapture) {
          event.currentTarget.setPointerCapture(event.pointerId);
        }
        this.container.style.cursor = 'grabbing';
      };

      this.onPointerMove = (event) => {
        if (!this.isDragging) return;
        const pointerDelta = event.clientY - this.lastPointerY;
        this.lastPointerY = event.clientY;
        if (Math.abs(pointerDelta) > 0.5) this.dragMoved = true;
        this.targetProgress -= pointerDelta / Math.max(this.verticalSpacing, 1);
      };

      this.stopDragging = (event) => {
        if (!this.isDragging) return;
        this.isDragging = false;
        if (event.currentTarget.releasePointerCapture && event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        this.container.style.cursor = dragEnabled ? 'grab' : 'default';
      };

      this.onClickCapture = (event) => {
        if (this.dragMoved) {
          event.preventDefault();
          event.stopPropagation();
          this.dragMoved = false;
        }
      };

      this.container.addEventListener('mouseenter', this.onMouseEnter);
      this.container.addEventListener('mouseleave', this.onMouseLeave);
      this.container.addEventListener('pointerdown', this.onPointerDown);
      this.container.addEventListener('pointermove', this.onPointerMove);
      this.container.addEventListener('pointerup', this.stopDragging);
      this.container.addEventListener('pointercancel', this.stopDragging);
      this.container.addEventListener('click', this.onClickCapture, true);
    }

    destroy() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  window.InfiniteSpiralApp = InfiniteSpiralApp;
})(window);
